import type { BuildIdentityAttributes } from "@/types/api";

declare const __KNOTFIN_CLIENT_VERSION__: string;
declare const __KNOTFIN_CLIENT_COMMIT__: string;
declare const __KNOTFIN_CLIENT_BUILD_DATE__: string;
declare const __KNOTFIN_CLIENT_BUILD_ID__: string;

export interface ClientBuildInfo {
  version: string;
  commit: string;
  buildDate: string;
  buildId: string;
}

export const CLIENT_BUILD_INFO: ClientBuildInfo = (() => {
  const version = cleanBuildValue(__KNOTFIN_CLIENT_VERSION__) || "0.1.0-dev";
  const commit = cleanBuildValue(__KNOTFIN_CLIENT_COMMIT__) || "unknown";
  const buildDate = cleanBuildValue(__KNOTFIN_CLIENT_BUILD_DATE__) || "unknown";
  const explicitBuildId = cleanBuildValue(__KNOTFIN_CLIENT_BUILD_ID__);
  return {
    version,
    commit,
    buildDate,
    buildId: explicitBuildId || buildIdentityId(version, commit, buildDate),
  };
})();

export function clientBuildHeaders(): Record<string, string> {
  return {
    "X-KnotFin-Client-Version": CLIENT_BUILD_INFO.version,
    "X-KnotFin-Client-Build-Id": CLIENT_BUILD_INFO.buildId,
    "X-KnotFin-Client-Build-Date": CLIENT_BUILD_INFO.buildDate,
    "X-KnotFin-Client-Commit": CLIENT_BUILD_INFO.commit,
  };
}

export function clientBuildDisplayLabel(info = CLIENT_BUILD_INFO): string {
  return `Web ${formatBuildDateStamp(info.buildDate)}`;
}

export function clientBuildCompactLabel(info = CLIENT_BUILD_INFO): string {
  return `Web ${formatBuildDateStamp(info.buildDate)}`;
}

export function clientBuildTitle(info = CLIENT_BUILD_INFO): string {
  return [
    `Web client build: ${info.buildId}`,
    `Stamp: ${formatBuildDateStamp(info.buildDate)}`,
    `Version: ${info.version}`,
    `Commit: ${info.commit}`,
    `Built: ${info.buildDate}`,
  ].join("\n");
}

export function isClientBuildStale(expected?: BuildIdentityAttributes | null, info = CLIENT_BUILD_INFO): boolean {
  const expectedBuildId = cleanBuildValue(expected?.build_id ?? undefined);
  return Boolean(expectedBuildId && info.buildId && expectedBuildId !== info.buildId);
}

function cleanBuildValue(value: string | undefined): string {
  return (value ?? "").trim();
}

function buildIdentityId(version: string, commit: string, buildDate: string): string {
  const parts = [];
  if (isMeaningfulBuildValue(commit)) parts.push(commit);
  if (isMeaningfulBuildValue(buildDate)) parts.push(buildDate);
  if (parts.length === 0) parts.push(version);
  return parts.join("@");
}

function isMeaningfulBuildValue(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== "" && trimmed.toLowerCase() !== "unknown";
}

export function formatBuildDateStamp(value: string | null | undefined): string {
  const normalized = value ?? "";
  if (!isMeaningfulBuildValue(normalized)) {
    return "dev";
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }
  const pad = (candidate: number) => String(candidate).padStart(2, "0");
  return [
    parsed.getUTCFullYear(),
    pad(parsed.getUTCMonth() + 1),
    pad(parsed.getUTCDate()),
    pad(parsed.getUTCHours()),
    pad(parsed.getUTCMinutes()),
  ].join("");
}

export function buildIdentityDateStamp(expected?: BuildIdentityAttributes | null): string | null {
  if (!expected?.date) {
    return null;
  }
  return formatBuildDateStamp(expected.date);
}

export function buildIdentityTitle(expected?: BuildIdentityAttributes | null): string {
  if (!expected) {
    return "Deployed web client build: unknown";
  }
  return [
    `Deployed web client build: ${expected.build_id ?? ""}`.trim(),
    `Stamp: ${buildIdentityDateStamp(expected) ?? "unknown"}`,
    `Version: ${expected.version ?? ""}`.trim(),
    `Commit: ${expected.commit ?? ""}`.trim(),
    `Built: ${expected.date ?? ""}`.trim(),
  ].join("\n");
}
